import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import PopupContainer from '../../../component/popup'
import InputField from '../../../component/formFields/InputField'
import FileUpload from '../../../component/formFields/FileUpload'
import MultiSelect from '../../../component/formFields/DropDown/Multiselect'
import DatePickerC from '../../../component/formFields/DatePicker'
import useForm from '../../../hooks/useForm'
import { PostPricingQuotation } from '../../../api/endpoints/UsersMedicalRequests'
import { DomainContext } from '../../../context/DomainContext'
import { succToast } from '../../../utils'
import ConfirmAlert from '../../../component/confirmAlert'
import moment from 'moment'

// Static block name options - can be replaced with API call later
const blockNameOptions = [
    { value: 'block1', label: 'Block 1' },
    { value: 'block2', label: 'Block 2' },
    { value: 'block3', label: 'Block 3' },
    { value: 'block4', label: 'Block 4' },
    { value: 'block5', label: 'Block 5' },
]

const PricingQuotationPopup = ({ modalId, requestDetails, requestId }) => {
    const { t } = useTranslation()
    const { formHandler, onSubmitFormData } = useForm()
    const { setLoading, ToggleReloadTable, loading, setDataLoading } = useContext(DomainContext)

    // Get min and max dates for DatePicker range
    const minDate = useMemo(() => {
        return requestDetails?.startDate ? new Date(requestDetails.startDate) : null
    }, [requestDetails?.startDate])

    const maxDate = useMemo(() => {
        return requestDetails?.endDate ? new Date(requestDetails.endDate) : null
    }, [requestDetails?.endDate])

    // Validate positive number for price
    useEffect(() => {
        const priceValue = formHandler.formValues?.price?.value
        if (priceValue !== undefined && priceValue !== null && priceValue !== '') {
            const numValue = parseFloat(priceValue)
            if (isNaN(numValue) || numValue <= 0) {
                formHandler.handleFormValues('price', {
                    ...formHandler.formValues?.price,
                    isError: true
                })
            } else {
                formHandler.handleFormValues('price', {
                    ...formHandler.formValues?.price,
                    isError: false
                })
            }
        } else if (formHandler.formValues?.price?.isRequired) {
            formHandler.handleFormValues('price', {
                ...formHandler.formValues?.price,
                isError: false
            })
        }
    }, [formHandler.formValues?.price?.value])

    const handleClose = () => {
        // Reset form
        Object.keys(formHandler.formValues).forEach(key => {
            formHandler.handleFormValues(key, { value: '', isRequired: true, isError: false })
        })
    }

    const handleDateChange = (dateRange) => {
        formHandler.handleFormValues('selectedDate', {
            value: dateRange, // { from: date, to: date } for range
            isRequired: true,
            isError: false
        })
    }

    // BlockName handler commented out for now
    // const handleBlockNameChange = (selectedOption) => {
    //     formHandler.handleFormValues('blockName', {
    //         value: selectedOption,
    //         isRequired: true,
    //         isError: false
    //     })
    // }

    const onApply = () => {
        // Get form values
        const quotationFile = formHandler.formValues?.quotationFile?.value
        const price = formHandler.formValues?.price?.value
        const dateRange = formHandler.formValues?.selectedDate?.value // { from: date, to: date }

        // Validate required fields
        if (!quotationFile || !price || !dateRange?.from || !dateRange?.to) {
            // Mark fields as error if they're required and empty
            if (!quotationFile && formHandler.formValues?.quotationFile?.isRequired) {
                formHandler.handleFormValues('quotationFile', {
                    ...formHandler.formValues?.quotationFile,
                    isError: true
                })
            }
            if (!price && formHandler.formValues?.price?.isRequired) {
                formHandler.handleFormValues('price', {
                    ...formHandler.formValues?.price,
                    isError: true
                })
            }
            if ((!dateRange?.from || !dateRange?.to) && formHandler.formValues?.selectedDate?.isRequired) {
                formHandler.handleFormValues('selectedDate', {
                    ...formHandler.formValues?.selectedDate,
                    isError: true
                })
            }
            return
        }

        const availableFromDate = moment(dateRange.from).format('YYYY-MM-DD')
        const availableToDate = moment(dateRange.to).format('YYYY-MM-DD')

        // Create FormData with proper structure
        const apiFormData = new FormData()
        apiFormData.append('medicalOrderId', requestId)

        // Append hospital request data
        const index = 0
        apiFormData.append(`HospitalRequestData[${index}].hospitalRequestedOrderId`, requestDetails?.id || 0)
        apiFormData.append(`HospitalRequestData[${index}].descriptionPDF`, quotationFile) // File appended directly
        apiFormData.append(`HospitalRequestData[${index}].availableFrom`, availableFromDate)
        apiFormData.append(`HospitalRequestData[${index}].availableTo`, availableToDate)

        // Append services prices
        // Note: Since services is now an array of strings, we'll use 0 for hospitalOrderServiceId
        // You may need to adjust this based on your actual business logic
        const priceIndex = 0
        apiFormData.append(`HospitalRequestData[${index}].servicesPrices[${priceIndex}].price`, parseFloat(price))


        ConfirmAlert(
            t("post-operation"),
            () => {
                setLoading(true)
                setDataLoading(true)
                PostPricingQuotation(apiFormData)
                    .then(res => {
                        ToggleReloadTable(prev => !prev)
                        succToast(t("Operation done successfully"))
                        handleClose()
                    })
                    .catch(() => {
                        // Error handled by RequestHandler
                    })
                    .finally(() => {
                        setDataLoading(false)
                        setLoading(false)
                    })
            }
        )
    }

    return (
        <PopupContainer
            name={`pricing_quotation_${requestId}`}
            recordID={requestId}
            title={"upload-pricing-quotation"}
            resetAllFields={handleClose}
            onApply={onApply}
            isLoading={loading}
            modalID={modalId}
            btnName={"Save"}
        >
            <div className="row">
                <div className="col-md-12">
                    <FileUpload
                        accept=".pdf,.doc,.docx"
                        value={formHandler.formValues?.quotationFile?.value}
                        setValue={(file) => formHandler.handleFormValues('quotationFile', {
                            value: file,
                            isRequired: true,
                            isError: false
                        })}
                        label={t("pricing-quotation-file")}
                        isRequired={true}
                    />
                </div>

                <InputField
                    name="price"
                    isLoading={false}
                    formHandler={formHandler}
                    type="number"
                    isRequired={true}
                    className="col-md-6"
                    defaultValue=""
                    min="0"
                    step="0.01"
                />

                {/* BlockName dropdown commented out for now */}
                {/* <div className="col-md-6">
                    <MultiSelect
                        label="blockName"
                        sOptions={formHandler.formValues?.blockName?.value || null}
                        setSOptions={handleBlockNameChange}
                        options={blockNameOptions}
                        isMulti={false}
                        isRequired={true}
                        isClearable={true}
                        className="col-md-12"
                    />
                </div> */}

                <div className="col-md-6">
                    <DatePickerC
                        label="selectedDate"
                        className="col-md-12"
                        isSingle={false}
                        dates={formHandler.formValues?.selectedDate?.value || null}
                        Apply={handleDateChange}
                        minDate={minDate}
                        maxDate={maxDate}
                        disabled={!minDate || !maxDate}
                        placeholder={t("select-date-range") || "Select date range"}
                    />
                </div>
            </div>
        </PopupContainer>
    )
}

export default PricingQuotationPopup

